import { useQuery } from '@tanstack/react-query';
import { getSupportedProducts } from '../../api/accounts.api';
import { getData } from '../../utils/functional/fp';
import { useAPIErrorHandler } from './useAPIErrorHandler';
import { useAuth } from './useAuth';

/**
 * The supported-products read, as the hook runs it and as code outside a component reads it through a query client:
 * one key and one function, so both share one cache entry.
 *
 * @param tenantId the tenant in the token (`useAuth().tokenTenantId`), which the cache entry is kept apart by.
 * @param country as `useSupportedProducts` takes it.
 */
export const supportedProductsQuery = (
  tenantId: string | null,
  country?: string
) => ({
  queryKey: ['supported-products', tenantId, country ?? null] as const,
  queryFn: () => getSupportedProducts({ params: { country } }).then(getData),
  staleTime: 60 * 60 * 1000,
  cacheTime: Infinity,
});

/**
 * @param country narrows the products to those sold in that country, as the API spells it. Without
 *   one, every country the tenant sells in.
 */
export const useSupportedProducts = (country?: string) => {
  const handleAPIError = useAPIErrorHandler();
  const { tokenTenantId } = useAuth();

  return useQuery({
    ...supportedProductsQuery(tokenTenantId, country),
    onError: handleAPIError('Unable to retrieve supported products.'),
  });
};
